import * as yup from 'yup';
import { KpiSection } from '../data-models/company-financials.data-model';
import { IField } from '../data-models/field2.data-model';

// ---------------------------------------------------------------------------
// Base schemas — mirror BE entities (Template, TemplateSection)
// ---------------------------------------------------------------------------

export function baseTemplateFields() {
  return {
    id: yup.number(),
    uuid: yup.string(),
    name: yup.string().required('Template name is required').default(''),
    meta: yup.mixed(),
    version: yup.number().nullable(),
    isDeleted: yup.boolean().nullable(),
    isManagedService: yup.boolean().nullable(),
  };
}

export function baseTemplateSchema() {
  return yup.object(baseTemplateFields());
}

export type BaseTemplate = yup.InferType<ReturnType<typeof baseTemplateSchema>>;

export function baseTemplateSectionFields() {
  return {
    id: yup.number(),
    templateId: yup.number(),
    type: yup.string(),
    name: yup.string(),
    meta: yup.mixed(),
  };
}

export function baseTemplateSectionSchema() {
  return yup.object(baseTemplateSectionFields());
}

export type BaseTemplateSection = yup.InferType<ReturnType<typeof baseTemplateSectionSchema>>;

// ---------------------------------------------------------------------------
// Custom (consolidated) template schemas — extend base with form-specific fields
// ---------------------------------------------------------------------------

export const CustomTemplateSectionType = {
  quantitative: 'quantitative',
  textual: 'textual',
  file: 'file',
  date: 'date',
} as const;
export type CustomTemplateSectionType =
  (typeof CustomTemplateSectionType)[keyof typeof CustomTemplateSectionType];

export function customTemplateSectionFields() {
  return {
    _key: yup.string().required(),
    id: baseTemplateSectionFields().id,
    type: yup
      .string()
      .oneOf([
        CustomTemplateSectionType.quantitative,
        CustomTemplateSectionType.textual,
        CustomTemplateSectionType.file,
        CustomTemplateSectionType.date,
      ] as const)
      .required(),
    sectionType: yup
      .string()
      .oneOf([KpiSection.actual, KpiSection.budget] as const)
      .required(),
    kpis: yup.array().of(yup.number().required()).required(),
  };
}

export function customTemplateSectionSchema() {
  return yup.object(customTemplateSectionFields());
}

export type TemplateSectionFormValues = yup.InferType<ReturnType<typeof customTemplateSectionSchema>>;

export function customKpiTemplateFields() {
  return {
    ...baseTemplateFields(),
    sections: yup.array().of(customTemplateSectionSchema()).default([]),
  };
}

export function customKpiTemplateSchema() {
  return yup.object(customKpiTemplateFields());
}

export type CustomKpiTemplateFormValues = yup.InferType<ReturnType<typeof customKpiTemplateSchema>>;

export function createCustomKpiTemplateDefaults(
  overrides: Partial<CustomKpiTemplateFormValues> = {}
): CustomKpiTemplateFormValues {
  return { ...customKpiTemplateSchema().getDefault(), sections: [], ...overrides };
}

// ---------------------------------------------------------------------------
// Consolidated template types
// ---------------------------------------------------------------------------

export const ConsolidatedTemplateVersion = 'consolidated' as const;

export function isConsolidatedTemplate(template: { meta?: unknown }): template is IConsolidatedTemplate {
  return (template.meta as Record<string, unknown>)?.version === ConsolidatedTemplateVersion;
}

export interface IConsolidatedSectionMeta {
  section: KpiSection;
  sortOrder: number;
  kpis: IField<unknown>[];
}

export interface IConsolidatedTemplateSection extends Omit<BaseTemplateSection, 'meta'> {
  templateId: number;
  type: CustomTemplateSectionType;
  meta: IConsolidatedSectionMeta;
}

export interface IConsolidatedTemplate extends Omit<BaseTemplate, 'meta'> {
  id: number;
  uuid: string;
  meta?: Record<string, unknown> | null;
  sections?: IConsolidatedTemplateSection[] | null;
}

// ---------------------------------------------------------------------------
// Section labels and menu options
// ---------------------------------------------------------------------------

export const SectionTypeLabel = {
  [CustomTemplateSectionType.quantitative]: 'Quantitative KPIs',
  [CustomTemplateSectionType.textual]: 'Text Question',
  [CustomTemplateSectionType.file]: 'File Upload',
  [CustomTemplateSectionType.date]: 'Date',
} as const;

export interface IMenuSectionOption {
  readonly type: CustomTemplateSectionType;
  readonly label: string;
}

export const MenuSectionOptions: readonly IMenuSectionOption[] = [
  { type: CustomTemplateSectionType.quantitative, label: SectionTypeLabel.quantitative },
  { type: CustomTemplateSectionType.textual, label: SectionTypeLabel.textual },
  { type: CustomTemplateSectionType.file, label: SectionTypeLabel.file },
  { type: CustomTemplateSectionType.date, label: SectionTypeLabel.date },
] as const;
